import React, { useState, useEffect, useMemo, useRef } from 'react';
import { PickModal } from '@pages/JIFI/orders/orders';

import { Badge } from '@shadcn/components/ui/badge';
import { Button } from '@shadcn/components/ui/button';
import { Input } from '@shadcn/components/ui/input';
import {
  Table as TableShadcn,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@shadcn/components/ui/table';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MultiSelect } from '@shadcn/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@shadcn/components/ui/select';
import { Dialog, DialogHeader, DialogContent, DialogDescription , DialogFooter, DialogTitle} from '@shadcn/components/ui/dialog';

import Breadcrumbs from '../Common/Breadcrumb';
import useQuery from '../../helpers/JIFI/useQuery';
import SKUToolTip from '../../pages/JIFI/helpers/SKUToolTip.js';
import useSKULevels from '../../pages/JIFI/helpers/useSKULevels.js';
import useUpcomingStock from '../../pages/JIFI/helpers/useUpcomingStock.js';
import useSKUStatus from '../../pages/JIFI/helpers/useSKUStatus.js';
import PrintPage from '../../pages/JIFI/monogram/PrintPage';
import PickList from '../../pages/JIFI/orders/PickList';

const columnHelper = createColumnHelper();

const perPageOptions = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
];

// Shared utility functions
export const fulfillmentToBadge = (fulfillment) => {
  if (fulfillment === 'unfulfilled') return 'warning';
  if (fulfillment === 'awaiting-stock') return 'secondary';
  const conversion = {
    fulfilled: 'success',
    partially_fulfilled: 'error',
  };
  return conversion[fulfillment];
};

export const pickToName = (pick) => {
  const conversion = {
    none: 'UNPICKED',
    monogram: 'TO-MONOGRAM',
    fulfillment: 'TO-SHIP',
    EWE: 'EWE',
    QV: 'QV',
    EWESYD: 'EWE Sydney',
    B360: 'B360',
    UNIS: 'UNIS',
    'EWE-MONO': 'EWE-MONO',
    AIRBOX: 'Airbox',
    'AIRBOX-MONO': 'Airbox Monogram',
    LAUNCH: 'Launch',
    'LAUNCH-MONO': 'Launch Monogram',
  };
  return conversion[pick] || pick;
};

export const pickToBadge = (pick) => {
  const conversion = {
    none: 'warning',
    monogram: 'info',
    fulfillment: 'success',
    EWE: 'success',
    QV: 'success',
    B360: 'success',
    EWESYD: 'success',
    UNIS: 'success',
    'EWE-MONO': 'info',
    AIRBOX: 'success',
    'AIRBOX-MONO': 'info',
    LAUNCH: 'success',
    'LAUNCH-MONO': 'info',
  };
  return conversion[pick] || 'success';
};

export const priorityColor = (priority) => {
  let check = priority?.toLowerCase();
  if (check === 'high') return 'danger';
  if (check === 'medium') return 'warning';
  if (check === 'low') return 'secondary';
  if (check !== 'low' && check !== 'high' && check !== 'medium') return 'info';
};

// Shared TablePagination component
export const TablePagination = ({
  count,
  page,
  setPage,
  perPage,
  setPerPage,
}) => {
  const pageCount = Math.ceil(count / perPage);
  const pages = [];
  
  for (let i = Math.max(1, page - 2); i <= Math.min(pageCount, page + 2); i++) {
    pages.push(i);
  }

  const handlePageChange = (page) => setPage(page);
  const handlePerPageChange = (newPerPage) => setPerPage(Number(newPerPage));

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
      <div className="w-full sm:w-36">
        <Select
          defaultValue={perPage}
          onValueChange={handlePerPageChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Items per page" />
          </SelectTrigger>
          <SelectContent>
            {perPageOptions.map((option, key) => (
              <SelectItem key={key} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
          className="min-w-[80px]"
        >
          Previous
        </Button>

        <div className="hidden sm:flex gap-2">
          {pages.map((pageNumber) => (
            <Button
              key={pageNumber}
              variant={pageNumber === page ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePageChange(pageNumber)}
              className="min-w-[40px]"
            >
              {pageNumber}
            </Button>
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page + 1)}
          disabled={page === pageCount}
          className="min-w-[80px]"
        >
          Next
        </Button>
      </div>

      <div className="text-sm text-gray-600">
        Page {page} of {pageCount} ({count} total)
      </div>
    </div>
  );
};

const UnfulfilledWarehouseOrders = ({
  apiEndpoint,
  linkBasePath,
  pageTitle,
  breadcrumbTitle = "Orders",
  getColumns,
  orderType = "order" 
}) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [refresh, setRefresh] = useState(true);
  const [selectedAllocatedWarehouse, setSelectedAllocatedWarehouse] = useState([]);
  const [allocatedWarehouseOptions, setAllocatedWarehouseOptions] = useState([]);
  const [selectedPickStatus, setSelectedPickStatus] = useState([]);
  const [selectedFinancialStatus, setSelectedFinancialStatus] = useState([]);
  const [selectedOrderSource, setSelectedOrderSource] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState([]);
  const [selectedPriority, setSelectedPriority] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  
  const [radioState, setRadioState] = useState({
    radio1: true,
    radio2: false,
    radio3: false,
  });


  const [selectAll, setSelectAll] = useState(false);
  const [selected, setSelected] = useState([]);


  const [modal, setModal] = useState(false);

  const togglemodal = () => setModal((prev) => !prev);


  const findSKULevel = useSKULevels();
  const findUpcomingStock = useUpcomingStock();
  const findSKUStatus = useSKUStatus();


  
  const getPickStatusOptions = () => {
    return   [
    { label: 'Unpicked', value: 'none' },
    { label: 'To Ship', value: 'fulfillment' },
    { label: 'To Monogram', value: 'monogram' },
    { label: 'EWE Monogram', value: 'EWE-MONO' },
    { label: 'EWESYD Monogram', value: 'EWESYD-MONO' },
    { label: 'EWE', value: 'EWE' },
    { label: 'EWE Sydney', value: 'EWESYD' },
    { label: 'B360', value: 'B360' },
    { label: 'UNIS', value: 'UNIS' },
    { label: 'Airbox', value: 'AIRBOX' },
    { label: 'Airbox Monogram', value: 'AIRBOX-MONO' },
    { label: 'Launch', value: 'LAUNCH' },
    { label: 'Launch Monogram', value: 'LAUNCH-MONO' },
      ];
  };

  const pickStatusOptions = getPickStatusOptions();

  
  const financialStatusOptions = [
    { label: 'Authorized', value: 'authorized' },
    { label: 'Paid', value: 'paid' },
    { label: 'Partially Paid', value: 'partially_paid' },
    { label: 'Partially Refunded', value: 'partially_refunded' },
    { label: 'Pending', value: 'pending' },
    { label: 'Refunded', value: 'refunded' },
    { label: 'Voided', value: 'voided' },
  ];

  
  const [orderSourceOptions, setOrderSourceOptions] = useState([]);

  
  const shippingOptions = [
    { label: 'Standard', value: 'standard' },
    { label: 'Express', value: 'express' },
    { label: 'Same Day', value: 'same-day' },
    { label: 'Next Day', value: 'next-day' },
    { label: 'QV Pickup', value: 'pickup' },
    { label: 'HQ Pickup', value: 'pickup_hq' },
    { label: 'Uber', value: 'Uber' },
    { label: 'Overnight', value: 'overnight' },
  ];

  
  const priorityOptions = [
    { label: 'Low', value: 'low' },
    { label: 'Medium', value: 'medium' },
    { label: 'High', value: 'high' },
  ];

  const [allocatedWarehousesData] = useQuery({
    url: `/orders/allocated-warehouses`,
  });

  
  const [orderSourceMappingsData] = useQuery({
    url: `/orderSourceMappings`,
    queryData: {
      includeShopify: true
    }
  });
  

  useEffect(() => {
    if (allocatedWarehousesData) {
      let warehouseOptions = allocatedWarehousesData.map((warehouse) => ({
        label: warehouse,
        value: warehouse,
      }));
      setAllocatedWarehouseOptions(warehouseOptions);
    }
  }, [allocatedWarehousesData]);

  useEffect(() => {
    if (orderSourceMappingsData) {
      let sourceOptions = orderSourceMappingsData
        .map((source) => ({
          label: source.name || source.label || source.value,
          value: source.orderSource || source.value || source.code || source.id,
        }))
        .filter((option) => option.value && option.label) 
        .filter((option, index, self) =>
          index === self.findIndex(o => o.value === option.value) 
        );

      
      

      setOrderSourceOptions(sourceOptions);
    }
  }, [orderSourceMappingsData]);

  
  const pickFilter = radioState.radio2
    ? 'monogram'
    : radioState.radio3
    ? 'fulfillment'
    : undefined;

  
  const [data, state] = useQuery({
    refresh,
    url: apiEndpoint,
    queryData: {
      page: page,
      perPage: perPage,
      ...(selectedAllocatedWarehouse && selectedAllocatedWarehouse.length > 0
        ? { allocatedWarehouse: selectedAllocatedWarehouse.join(',') }
        : {}),
      ...(selectedPickStatus && selectedPickStatus.length > 0
        ? { filterPickStatus: selectedPickStatus.join(',') }
        : {}),
      ...(orderType !== "corp" && selectedFinancialStatus && selectedFinancialStatus.length > 0
        ? { filterFinancialStatus: selectedFinancialStatus.join(',') }
        : {}),
      ...(orderType !== "corp" && selectedOrderSource && selectedOrderSource.length > 0
        ? { filterSource: selectedOrderSource.join(',') }
        : {}),
      ...(orderType !== "corp" && selectedShipping && selectedShipping.length > 0
        ? { filterShipping: selectedShipping.join(',') }
        : {}),
      ...(orderType === "corp" && selectedPriority && selectedPriority.length > 0
        ? { filterPriority: selectedPriority.join(',') }
        : {}),
      ...(pickFilter ? { pickCategory: pickFilter } : {}),
      ...(searchTerm ? { searchTerm } : {}),
      ...(startDate ? { filterStartDate: startDate } : {}),
      ...(endDate ? { filterEndDate: endDate } : {}),
    },
  });

  const orders = data && data.data;
  const count = data && data.count;

  const handleSelectAllocatedWarehouse = (selected) => {
    setSelectedAllocatedWarehouse(selected);
    setPage(1);
  };

  const handleSelectPickStatus = (selected) => {
    setSelectedPickStatus(selected);
    setPage(1);
    if (selected && selected.length > 0) {
      setRadioState({
        radio1: true,
        radio2: false,
        radio3: false,
      });
    }
  };

  const handleSelectFinancialStatus = (selected) => {
    setSelectedFinancialStatus(selected);
    setPage(1);
  };

  const handleSelectOrderSource = (selected) => {
    setSelectedOrderSource(selected);
    setPage(1);
  };

  const handleSelectShipping = (selected) => {
    setSelectedShipping(selected);
    setPage(1);
  };

  const handleSelectPriority = (selected) => {
    setSelectedPriority(selected);
    setPage(1);
  };

  
  const handleRadioStateChange = (value) => {
    setRadioState(value);
    setSelected([])
    setPage(1);
    if (value.radio2 || value.radio3) {
      setSelectedPickStatus([]);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setPage(1);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setPage(1);
  };

  const refreshData = () => {
    setRefresh((prev) => !prev);
  };

  // Selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelected([]);
    } else {
      setSelected(orders?.map(order => order._id) || []);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectOrder = (orderId) => {
    setSelected(prevSelected =>
      prevSelected.includes(orderId)
        ? prevSelected.filter(id => id !== orderId)
        : [...prevSelected, orderId]
    );
  };


  useEffect(() => {
    if (orders && orders.length > 0) {
      setSelectAll(selected.length === orders.length);
    }
  }, [selected, orders]);

  const columns = useMemo(
    () => {
      const baseProps = {
        linkBasePath,
        findSKULevel,
        findUpcomingStock,
        findSKUStatus,
        columnHelper,
      };

      
      if (orderType !== "corp") {
        return getColumns({
          ...baseProps,
          selectAll,
          selected,
          onSelectAll: handleSelectAll,
          onSelectOrder: handleSelectOrder
        });
      } else {
        return getColumns(baseProps);
      }
    },
    [linkBasePath, findSKULevel, findUpcomingStock, findSKUStatus, getColumns, selectAll, selected, orderType]
  );

  const dataTables = orders?.map((order) => {
    let skuCount = {};
    order.line_items.forEach((val) => {
      skuCount[val.sku] = (skuCount[val.sku] || 0) + val.quantity;
    });

    const lineSum = Object.keys(skuCount).map((sku) => ({
      sku,
      quantity: skuCount[sku],
    }));

    return { ...order, lineSum };
  });

  const table = useReactTable({
    data: dataTables ?? [],
    columns,
    rowCount: count,
    state: {
      pagination: {
        pageIndex: page - 1,
        pageSize: perPage,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <>
      <div className="page-content">
        <div className="w-full px-3 mx-auto xl:max-w-[85%]">
          <Breadcrumbs 
            title={breadcrumbTitle} 
          />

          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="card-title mb-0">{pageTitle}</h4>

                
                {orderType !== "corp" && selected.length > 0 &&   radioState.radio1 === false && (
                  <div>
                    <Button variant="primary" onClick={togglemodal}>
                      Generate Picklist ({selected.length} orders)
                    </Button>
                  </div>
                )}
              </div>

              
              {orderType !== "corp" && (
                <div className="mb-4">
                  <label className="text-sm font-medium mb-2 block">PICK CATEGORY</label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      className={`px-4 py-2 rounded-md transition-colors ${
                        radioState.radio1
                          ? '!bg-primary text-primary-foreground hover:bg-primary/90'
                          : 'bg-background border border-black hover:bg-gray-100'
                      }`}
                      onClick={() =>
                        handleRadioStateChange({
                          radio1: true,
                          radio2: false,
                          radio3: false,
                        })
                      }
                    >
                      None
                    </button>
                    <button
                      className={`px-4 py-2 rounded-md transition-colors ${
                        radioState.radio2
                          ? '!bg-primary text-primary-foreground hover:bg-primary/90'
                          : 'bg-background border border-black hover:bg-gray-100'
                      }`}
                      onClick={() =>
                        handleRadioStateChange({
                          radio2: true,
                          radio1: false,
                          radio3: false,
                        })
                      }
                    >
                      Monogram
                    </button>
                    <button
                      className={`px-4 py-2 rounded-md transition-colors ${
                        radioState.radio3
                          ? '!bg-primary text-primary-foreground hover:bg-primary/90'
                          : 'bg-background border border-black hover:bg-gray-100'
                      }`}
                      onClick={() =>
                        handleRadioStateChange({
                          radio3: true,
                          radio2: false,
                          radio1: false,
                        })
                      }
                    >
                      Fulfillment
                    </button>
                  </div>
                </div>
              )}

              
              {orderType !== "corp" ? (
                <div className="space-y-4 mb-4">
                  {/* Regular Orders - First row: Search, Start Date, End Date, Warehouse */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="form-label">Search Orders</label>
                      <Input
                        type="text"
                        placeholder="Search orders..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="form-control"
                      />
                    </div>

                    <div>
                      <label className="form-label">Start Date</label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={handleStartDateChange}
                        className="form-control"
                      />
                    </div>

                    <div>
                      <label className="form-label">End Date</label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={handleEndDateChange}
                        className="form-control"
                      />
                    </div>

                    <div>
                      <label className="form-label">Allocated Warehouse</label>
                      <MultiSelect
                        options={allocatedWarehouseOptions}
                        onValueChange={handleSelectAllocatedWarehouse}
                        defaultValue={selectedAllocatedWarehouse || []}
                        placeholder="Select warehouses..."
                        variant="inverted"
                        animation={2}
                        maxCount={3}
                      />
                    </div>
                  </div>

                  {/* Regular Orders - Second row: Pick Status, Financial Status, Order Source, Shipping */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="form-label">Pick Status</label>
                      <MultiSelect
                        options={pickStatusOptions}
                        onValueChange={handleSelectPickStatus}
                        defaultValue={selectedPickStatus || []}
                        placeholder="Select pick status..."
                        variant="inverted"
                        animation={2}
                        maxCount={3}
                      />
                    </div>

                    <div>
                      <label className="form-label">Financial Status</label>
                      <MultiSelect
                        options={financialStatusOptions}
                        onValueChange={handleSelectFinancialStatus}
                        defaultValue={selectedFinancialStatus || []}
                        placeholder="Select financial status..."
                        variant="inverted"
                        animation={2}
                        maxCount={3}
                      />
                    </div>

                    <div>
                      <label className="form-label">Order Source</label>
                      <MultiSelect
                        options={orderSourceOptions}
                        onValueChange={handleSelectOrderSource}
                        value={selectedOrderSource || []}
                        placeholder="Select order source..."
                        variant="inverted"
                        animation={2}
                        maxCount={3}
                      />
                    </div>

                    <div>
                      <label className="form-label">Shipping</label>
                      <MultiSelect
                        options={shippingOptions}
                        onValueChange={handleSelectShipping}
                        defaultValue={selectedShipping || []}
                        placeholder="Select shipping..."
                        variant="inverted"
                        animation={2}
                        maxCount={3}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                
                <div className="space-y-4 mb-4">
                
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">Search Orders</label>
                      <Input
                        type="text"
                        placeholder="Search orders..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className="form-control"
                      />
                    </div>

                    <div>
                      <label className="form-label">Start Date</label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={handleStartDateChange}
                        className="form-control"
                      />
                    </div>

                    <div>
                      <label className="form-label">End Date</label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={handleEndDateChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">Allocated Warehouse</label>
                      <MultiSelect
                        options={allocatedWarehouseOptions}
                        onValueChange={handleSelectAllocatedWarehouse}
                        defaultValue={selectedAllocatedWarehouse || []}
                        placeholder="Select warehouses..."
                        variant="inverted"
                        animation={2}
                        maxCount={3}
                      />
                    </div>

                    <div>
                      <label className="form-label">Pick Status</label>
                      <MultiSelect
                        options={pickStatusOptions}
                        onValueChange={handleSelectPickStatus}
                        defaultValue={selectedPickStatus || []}
                        placeholder="Select pick status..."
                        variant="inverted"
                        animation={2}
                        maxCount={3}
                      />
                    </div>

                    <div>
                      <label className="form-label">Priority</label>
                      <MultiSelect
                        options={priorityOptions}
                        onValueChange={handleSelectPriority}
                        defaultValue={selectedPriority || []}
                        placeholder="Select priority..."
                        variant="inverted"
                        animation={2}
                        maxCount={3}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Table */}
              <div className="overflow-x-auto">
                <TableShadcn className="min-w-full">
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id} className="!px-4">
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  
                  {state === 'loading' || state.loading ? (
                    <TableSkeleton perPage={perPage} columns={columns.length} />
                  ) : (
                    <TableBody>
                      {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                          <React.Fragment key={row.id}>
                            <TableRow
                            className={` ${row.original?.flagged ? 'bg-red-50' : ''} ${!(row.original?.financial_status === 'authorized' && ['shopify_nz', 'shopify_test', 'shopify_au', 'shopify_uk', 'shopify_us', 'shopify_ca'].includes(row.original.order_source)) ? '' : 'opacity-80 bg-gray-300'}`}
                              
                            >
                              {row.getVisibleCells().map((cell) => (
                                <TableCell key={cell.id} className={`!px-4`}>
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </TableCell>
                              ))}
                            </TableRow>
                            
                            {row.getIsExpanded() &&
                              row.original?.lineSum?.length > 1 &&
                              row.original.lineSum.slice(1).map((item, index) => {
                                const skuColumnIndex = columns.findIndex(col => 
                                  col.accessorFn?.toString().includes('skus') || 
                                  col.accessorKey === 'skus' ||
                                  col.id === 'skus'
                                );
                                return (
                                  <TableRow key={`${row.id}-expanded-${index}`}>
                                    {columns.map((column, colIndex) => (
                                      <TableCell key={`${row.id}-expanded-${index}-${colIndex}`}>
                                        {colIndex === skuColumnIndex ? (
                                          <Badge className="font-size-12 badge-soft-primary" variant="primary">
                                            <SKUToolTip 
                                              sku={item.sku} 
                                              findSKULevel={findSKULevel} 
                                              findUpcomingStock={findUpcomingStock} 
                                              findSKUStatus={findSKUStatus} 
                                            /> x {item.quantity}
                                          </Badge>
                                        ) : null}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                );
                              })}
                          </React.Fragment>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={columns.length} className="h-24 text-center">
                            No results.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  )}
                </TableShadcn>
              </div>

              {/* Pagination */}
              <div className="mt-4">
                <TablePagination
                  count={count || 0}
                  page={page}
                  setPage={setPage}
                  perPage={perPage}
                  setPerPage={setPerPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>


      
      {orderType !== "corp" && (
        <PickModal
          refresh={refreshData}
          monogram={radioState.radio2 ? true : false}
          togglemodal={togglemodal}
          modal={modal}
          selectedIds={orders?.filter(order => selected.includes(order._id)) || []}
        />
      )}
    </>
  );
};




export default UnfulfilledWarehouseOrders;
